import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  deleteApplication,
  deleteInquiry,
  getDashboardApplications,
  getDashboardInquiries,
  getDashboardStats,
  scheduleApplication,
  updateApplicationStatus,
  updateInquiryStatus,
} from '../api/client';
import { APPLICATION_LABELS } from '../utils/serviceApplications';
import { useAuth } from '../context/AuthContext';
import BrandManagement from '../components/cms/BrandManagement';
import VehicleManagement from '../components/cms/VehicleManagement';
import DataTablePagination from '../components/common/DataTablePagination';
import ScheduleModal from '../components/common/ScheduleModal';
import ApplicationDetail from '../components/ApplicationDetail';
import InquiryDetail from '../components/InquiryDetail';
import useLiveRefresh from '../hooks/useLiveRefresh';
import AvailabilityManager from '../components/AvailabilityManager';
import SearchableSelect from '../components/common/SearchableSelect';

const applicationStatusOptions = ['pending', 'contacted', 'scheduled', 'completed', 'cancelled'];

const statusClass = (status) => {
  const map = {
    new: 'status-new',
    pending: 'status-pending',
    contacted: 'status-contacted',
    answered: 'status-answered',
    closed: 'status-closed',
    scheduled: 'status-scheduled',
    completed: 'status-completed',
    cancelled: 'status-cancelled',
  };
  return map[status] || 'status-pending';
};

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-PK', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getDisplayVehicle = (row) => {
  if (!row) return '';
  const name = row.vehicle_name || '';
  const variant = row.vehicle_variant || '';
  if (variant && !name.toLowerCase().includes(variant.toLowerCase())) {
    return `${name} ${variant}`.trim();
  }
  return name;
};

const StatCard = ({ icon, label, value, accent }) => (
  <div className={`dash-stat-card dash-stat-${accent}`}>
    <div className="dash-stat-icon">
      <i className={`fas ${icon}`} />
    </div>
    <div className="dash-stat-body">
      <span className="dash-stat-label">{label}</span>
      <strong className="dash-stat-value">{value ?? '—'}</strong>
    </div>
  </div>
);

const EmptyState = ({ icon, title, text, onClear }) => (
  <div className="dash-empty">
    <div className="dash-empty-icon">
      <i className={`fas ${icon}`} />
    </div>
    <h3>{title}</h3>
    <p>{text}</p>
    {onClear && (
      <button
        type="button"
        className="dash-btn-reset-filters"
        onClick={onClear}
        style={{ marginTop: 12 }}
      >
        <i className="fas fa-rotate-left" /> Clear search & filters
      </button>
    )}
  </div>
);

const LegacyApplicationRedirect = () => {
  const { applicationId } = useParams();
  return <Navigate to={`/dashboard/applications/${applicationId}`} replace />;
};

const DashboardPage = () => {
  const { user, isAdmin, logout, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Scheduling Modal State
  const [selectedAppForSchedule, setSelectedAppForSchedule] = useState(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

  // Status Change Confirmation Modal State (Closed, Completed, Cancelled)
  const [statusConfirm, setStatusConfirm] = useState(null);

  // Inquiries Table State
  const [inquirySearch, setInquirySearch] = useState('');
  const [inquiryStatusFilter, setInquiryStatusFilter] = useState('all');
  const [inquiryDateFilter, setInquiryDateFilter] = useState('');
  const [inquiryPage, setInquiryPage] = useState(1);
  const [inquiryPageSize, setInquiryPageSize] = useState(10);

  // Test Drives Table State
  const [testDriveSearch, setTestDriveSearch] = useState('');
  const [testDriveStatusFilter, setTestDriveStatusFilter] = useState('all');
  const [testDriveDateFilter, setTestDriveDateFilter] = useState('');
  const [testDriveVehicleFilter, setTestDriveVehicleFilter] = useState('all');
  const [testDrivePage, setTestDrivePage] = useState(1);
  const [testDrivePageSize, setTestDrivePageSize] = useState(10);
  const [applicationTypeFilter, setApplicationTypeFilter] = useState('all');

  const isInquiriesRoute =
    location.pathname === '/dashboard/inquiries' ||
    location.pathname === '/dashboard/inquiries/';
  const isTestDrivesRoute = location.pathname.startsWith('/dashboard/applications');
  const isAvailabilityRoute = location.pathname.startsWith('/dashboard/availability');
  const isBrandsRoute = location.pathname.startsWith('/dashboard/marketplace/brands');
  const isVehiclesRoute = location.pathname.startsWith('/dashboard/marketplace/vehicles');
  const isMarketplaceRoute = isBrandsRoute || isVehiclesRoute;

  // Phase 2: Marketplace sidebar section is COLLAPSED by default
  const [marketplaceOpen, setMarketplaceOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  // Backward compatibility: redirect legacy ?tab= query params to clean routes
  useEffect(() => {
    const legacyTab = searchParams.get('tab');
    if (legacyTab === 'brands') {
      navigate('/dashboard/marketplace/brands', { replace: true });
    } else if (legacyTab === 'vehicles') {
      navigate('/dashboard/marketplace/vehicles', { replace: true });
    } else if (legacyTab === 'applications') {
      navigate('/dashboard/applications', { replace: true });
    } else if (legacyTab === 'inquiries') {
      navigate('/dashboard/inquiries', { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  const handleToggleMarketplace = (e) => {
    e.preventDefault();
    setMarketplaceOpen((prev) => !prev);
  };

  const handleNavigateInquiries = () => {
    if (isInquiriesRoute) return;
    if (hasUnsavedChanges) {
      const confirmDiscard = window.confirm(
        'You have unsaved changes in this vehicle record. Are you sure you want to discard your edits?',
      );
      if (!confirmDiscard) return;
    }
    setHasUnsavedChanges(false);
    navigate('/dashboard/inquiries');
  };

  const handleNavigateTestDrives = () => {
    if (/^\/dashboard\/applications\/?$/.test(location.pathname)) return;
    if (hasUnsavedChanges) {
      const confirmDiscard = window.confirm(
        'You have unsaved changes in this vehicle record. Are you sure you want to discard your edits?',
      );
      if (!confirmDiscard) return;
    }
    setHasUnsavedChanges(false);
    navigate('/dashboard/applications');
  };

  const handleNavigateBrands = () => {
    if (location.pathname === '/dashboard/marketplace/brands') {
      return;
    }
    if (hasUnsavedChanges) {
      const confirmDiscard = window.confirm(
        'You have unsaved changes in this vehicle record. Are you sure you want to discard your edits?',
      );
      if (!confirmDiscard) return;
    }
    setHasUnsavedChanges(false);
    navigate('/dashboard/marketplace/brands');
  };

  const handleNavigateVehicles = () => {
    if (location.pathname === '/dashboard/marketplace/vehicles') {
      return;
    }
    if (hasUnsavedChanges) {
      const confirmDiscard = window.confirm(
        'You have unsaved changes in this vehicle record. Are you sure you want to discard your edits?',
      );
      if (!confirmDiscard) return;
    }
    setHasUnsavedChanges(false);
    navigate('/dashboard/marketplace/vehicles');
  };

  const handleLogout = () => {
    if (hasUnsavedChanges) {
      const confirmDiscard = window.confirm(
        'You have unsaved changes in this vehicle record. Are you sure you want to discard your edits?',
      );
      if (!confirmDiscard) return;
    }
    setHasUnsavedChanges(false);
    logout();
  };

  const handleBackToSite = (e) => {
    if (hasUnsavedChanges) {
      const confirmDiscard = window.confirm(
        'You have unsaved changes in this vehicle record. Are you sure you want to discard your edits?',
      );
      if (!confirmDiscard) {
        e.preventDefault();
      } else {
        setHasUnsavedChanges(false);
      }
    }
  };

  // silent: background refresh (no spinner, no error banner; what is on screen stays if it fails)
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const [s, i, a] = await Promise.all([
        getDashboardStats(),
        getDashboardInquiries(),
        getDashboardApplications(),
      ]);
      setStats(s);
      setInquiries(i);
      setApplications(a);
      setError('');
    } catch (err) {
      console.error(err);
      if (!silent) setError('Could not load dashboard data.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  // Lists, counts, badges and open detail pages stay current (changes made in the ops hub show up too).
  // Paused while a status confirmation or the schedule form is open.
  useLiveRefresh(() => load({ silent: true }), {
    enabled: Boolean(user) && !isScheduleModalOpen && !statusConfirm,
  });

  // Phase 3: Inquiry Status Handling with Terminal State Protection
  const executeInquiryStatusChange = async (inquiryId, newStatus) => {
    try {
      const updated = await updateInquiryStatus(inquiryId, newStatus);
      setInquiries((prev) =>
        prev.map((item) => (item.id === inquiryId ? updated : item))
      );
      getDashboardStats().then(setStats).catch(console.error);
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.detail || 'Failed to update inquiry status.';
      alert(`Status update failed: ${detail}`);
      getDashboardInquiries().then(setInquiries).catch(console.error);
    }
  };

  const handleInquiryStatusChange = (inquiryId, newStatus) => {
    if (newStatus === 'closed') {
      setStatusConfirm({
        type: 'inquiry',
        id: inquiryId,
        status: newStatus,
        title: 'Close Inquiry',
        message: 'Are you sure you want to mark this inquiry as Closed?',
        warning: 'Closing this inquiry is permanent and cannot be undone.',
        confirmLabel: 'Confirm & Close',
        isDanger: true,
      });
      return;
    }
    executeInquiryStatusChange(inquiryId, newStatus);
  };

  // Phase 4 & 5: Test Drive Status Handling with Terminal State Protection & Modal
  const executeApplicationStatusChange = async (applicationId, newStatus) => {
    try {
      const updated = await updateApplicationStatus(applicationId, newStatus);
      setApplications((prev) =>
        prev.map((item) => (item.id === applicationId ? updated : item))
      );
      getDashboardStats().then(setStats).catch(console.error);
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.detail || 'Failed to update test-drive status.';
      alert(`Status update failed: ${detail}`);
      getDashboardApplications().then(setApplications).catch(console.error);
    }
  };

  const handleApplicationStatusChange = (applicationId, newStatus, row) => {
    if (newStatus === 'scheduled') {
      setSelectedAppForSchedule(row);
      setIsScheduleModalOpen(true);
      return;
    }

    if (newStatus === 'completed' || newStatus === 'cancelled') {
      setStatusConfirm({
        type: 'application',
        id: applicationId,
        status: newStatus,
        title: newStatus === 'completed' ? 'Complete application' : 'Cancel application',
        message: `Are you sure you want to mark this application as ${newStatus.toUpperCase()}?`,
        warning: `Marking this application as ${newStatus.toUpperCase()} is permanent and cannot be undone.`,
        confirmLabel: `Confirm & ${newStatus === 'completed' ? 'Complete' : 'Cancel'}`,
        isDanger: newStatus === 'cancelled',
      });
      return;
    }

    executeApplicationStatusChange(applicationId, newStatus);
  };

  // Delete: ask first (same confirmation modal), then remove from the lists and leave a deleted detail page.
  const requestDelete = (kind, row) => {
    if (!row) return;
    const isInquiry = kind === 'inquiry';
    setStatusConfirm({
      type: isInquiry ? 'delete-inquiry' : 'delete-application',
      id: row.id,
      title: isInquiry ? 'Delete inquiry' : 'Delete application',
      message: `Delete ${isInquiry ? 'inquiry' : APPLICATION_LABELS[row.application_type] || 'application'} #${row.id} from ${row.customer_name || 'customer'}?`,
      warning: isInquiry
        ? 'The inquiry is removed permanently, here and in the ops hub.'
        : 'The application, its uploaded documents and its schedule history are removed permanently, here and in the ops hub.',
      confirmLabel: 'Delete',
      isDanger: true,
      icon: 'fa-trash',
    });
  };

  const executeDelete = async (type, id) => {
    const isInquiry = type === 'delete-inquiry';
    try {
      if (isInquiry) {
        await deleteInquiry(id);
        setInquiries((prev) => prev.filter((item) => item.id !== id));
      } else {
        await deleteApplication(id);
        setApplications((prev) => prev.filter((item) => item.id !== id));
      }
      const detailPrefix = isInquiry ? '/dashboard/inquiries/' : '/dashboard/applications/';
      if (location.pathname === `${detailPrefix}${id}`) {
        navigate(isInquiry ? '/dashboard/inquiries' : '/dashboard/applications');
      }
      getDashboardStats().then(setStats).catch(console.error);
    } catch (err) {
      console.error(err);
      alert(`Delete failed: ${err.response?.data?.detail || 'Please try again.'}`);
      load({ silent: true });
    }
  };

  const handleConfirmStatusChange = async () => {
    if (!statusConfirm) return;
    const { type, id, status } = statusConfirm;
    setStatusConfirm(null);
    if (type === 'delete-inquiry' || type === 'delete-application') {
      await executeDelete(type, id);
      return;
    }
    if (type === 'inquiry') {
      await executeInquiryStatusChange(id, status);
    } else if (type === 'application') {
      await executeApplicationStatusChange(id, status);
    }
  };

  const handleCancelStatusChange = () => {
    setStatusConfirm(null);
    setInquiries((prev) => [...prev]);
    setApplications((prev) => [...prev]);
  };

  const handleOpenScheduleModal = (row) => {
    setSelectedAppForSchedule(row);
    setIsScheduleModalOpen(true);
  };

  const handleScheduleSubmit = async (applicationId, schedulePayload) => {
    const updated = await scheduleApplication(applicationId, schedulePayload);
    setApplications((prev) =>
      prev.map((item) => (item.id === applicationId ? updated : item))
    );
    getDashboardStats().then(setStats).catch(console.error);
  };

  // Inquiries Filtering & Pagination Logic
  const filteredInquiries = useMemo(() => {
    return inquiries.filter((row) => {
      if (inquiryStatusFilter !== 'all' && (row.status || '').toLowerCase() !== inquiryStatusFilter.toLowerCase()) {
        return false;
      }
      if (inquiryDateFilter) {
        const rowDate = row.created_at ? new Date(row.created_at).toISOString().split('T')[0] : '';
        if (rowDate !== inquiryDateFilter) return false;
      }
      if (inquirySearch.trim()) {
        const q = inquirySearch.trim().toLowerCase();
        const dispVehicle = getDisplayVehicle(row).toLowerCase();
        const rawVehicle = (row.vehicle_name || '').toLowerCase();
        const rawVariant = (row.vehicle_variant || '').toLowerCase();
        const matchName = (row.customer_name || '').toLowerCase().includes(q);
        const matchEmail = (row.email || '').toLowerCase().includes(q);
        const matchPhone = (row.phone || '').toLowerCase().includes(q);
        const matchVehicle =
          dispVehicle.includes(q) || rawVehicle.includes(q) || rawVariant.includes(q);
        const matchMsg = (row.message || '').toLowerCase().includes(q);
        const matchStatus = (row.status || '').toLowerCase().includes(q);
        const matchId = String(row.id || '').includes(q) || `#${row.id}`.includes(q);
        const matchDate = formatDate(row.created_at).toLowerCase().includes(q);
        if (
          !matchName &&
          !matchEmail &&
          !matchPhone &&
          !matchVehicle &&
          !matchMsg &&
          !matchStatus &&
          !matchId &&
          !matchDate
        ) {
          return false;
        }
      }
      return true;
    });
  }, [inquiries, inquiryStatusFilter, inquiryDateFilter, inquirySearch]);

  const pagedInquiries = useMemo(() => {
    const start = (inquiryPage - 1) * inquiryPageSize;
    return filteredInquiries.slice(start, start + inquiryPageSize);
  }, [filteredInquiries, inquiryPage, inquiryPageSize]);

  // Unique Vehicles for Test Drive Filter Dropdown
  const uniqueTestDriveVehicles = useMemo(() => {
    const set = new Set();
    applications.forEach((app) => {
      const vName = getDisplayVehicle(app);
      if (vName && vName.trim()) {
        set.add(vName.trim());
      }
    });
    return Array.from(set).sort();
  }, [applications]);

  // Test Drives Filtering & Pagination Logic
  const filteredApplications = useMemo(() => {
    return applications.filter((row) => {
      const appType = row.application_type || 'testdrive';
      if (applicationTypeFilter !== 'all' && appType !== applicationTypeFilter) {
        return false;
      }
      if (testDriveStatusFilter !== 'all' && (row.status || '').toLowerCase() !== testDriveStatusFilter.toLowerCase()) {
        return false;
      }
      if (testDriveDateFilter) {
        if ((row.scheduled_date || '') !== testDriveDateFilter) return false;
      }
      if (testDriveVehicleFilter !== 'all') {
        const dispVehicle = getDisplayVehicle(row).toLowerCase();
        if (dispVehicle !== testDriveVehicleFilter.toLowerCase()) {
          return false;
        }
      }
      if (testDriveSearch.trim()) {
        const q = testDriveSearch.trim().toLowerCase();
        const dispVehicle = getDisplayVehicle(row).toLowerCase();
        const rawVehicle = (row.vehicle_name || '').toLowerCase();
        const rawVariant = (row.vehicle_variant || '').toLowerCase();
        const matchName = (row.customer_name || '').toLowerCase().includes(q);
        const matchEmail = (row.email || '').toLowerCase().includes(q);
        const matchPhone = (row.phone || '').toLowerCase().includes(q);
        const matchVehicle =
          dispVehicle.includes(q) || rawVehicle.includes(q) || rawVariant.includes(q);
        const matchStatus = (row.status || '').toLowerCase().includes(q);
        const matchCity = (row.city || '').toLowerCase().includes(q);
        const matchPrefDate = (row.preferred_date || '').toLowerCase().includes(q);
        const matchPrefTime = (row.preferred_time || '').toLowerCase().includes(q);
        const matchSchedDate = (row.scheduled_date || '').toLowerCase().includes(q);
        const matchSchedTime = (row.scheduled_time || '').toLowerCase().includes(q);
        const matchNotes = (row.notes || '').toLowerCase().includes(q) || (row.schedule_notes || '').toLowerCase().includes(q);
        const matchType = appType.includes(q) || (APPLICATION_LABELS[appType] || '').toLowerCase().includes(q);
        const matchDate = formatDate(row.created_at).toLowerCase().includes(q);
        const matchId = String(row.id || '').includes(q) || `#${row.id}`.includes(q);
        if (
          !matchName &&
          !matchEmail &&
          !matchPhone &&
          !matchVehicle &&
          !matchStatus &&
          !matchCity &&
          !matchPrefDate &&
          !matchPrefTime &&
          !matchSchedDate &&
          !matchSchedTime &&
          !matchNotes &&
          !matchType &&
          !matchDate &&
          !matchId
        ) {
          return false;
        }
      }
      return true;
    });
  }, [applications, applicationTypeFilter, testDriveStatusFilter, testDriveDateFilter, testDriveVehicleFilter, testDriveSearch]);

  const pagedApplications = useMemo(() => {
    const start = (testDrivePage - 1) * testDrivePageSize;
    return filteredApplications.slice(start, start + testDrivePageSize);
  }, [filteredApplications, testDrivePage, testDrivePageSize]);

  // Reset pagination on filter or search changes
  const handleInquirySearchChange = (val) => {
    setInquirySearch(val);
    setInquiryPage(1);
  };
  const handleInquiryStatusFilterChange = (val) => {
    setInquiryStatusFilter(val);
    setInquiryPage(1);
  };
  const handleInquiryDateFilterChange = (val) => {
    setInquiryDateFilter(val);
    setInquiryPage(1);
  };
  const handleClearInquiryFilters = () => {
    setInquirySearch('');
    setInquiryStatusFilter('all');
    setInquiryDateFilter('');
    setInquiryPage(1);
  };

  const handleTestDriveSearchChange = (val) => {
    setTestDriveSearch(val);
    setTestDrivePage(1);
  };
  const handleTestDriveStatusFilterChange = (val) => {
    setTestDriveStatusFilter(val);
    setTestDrivePage(1);
  };
  const handleTestDriveDateFilterChange = (val) => {
    setTestDriveDateFilter(val);
    setTestDrivePage(1);
  };
  const handleTestDriveVehicleFilterChange = (val) => {
    setTestDriveVehicleFilter(val);
    setTestDrivePage(1);
  };
  const handleClearTestDriveFilters = () => {
    setTestDriveSearch('');
    setTestDriveStatusFilter('all');
    setTestDriveDateFilter('');
    setTestDriveVehicleFilter('all');
    setApplicationTypeFilter('all');
    setTestDrivePage(1);
  };

  if (authLoading) {
    return (
      <div className="dash-shell portal-autocompare">
        <div className="dash-loading">
          <div className="dash-spinner" />
          <p>Loading dashboard…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    const nextPath = location.pathname + location.search;
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(
          nextPath.startsWith('/dashboard') ? nextPath : '/dashboard/inquiries'
        )}`}
        replace
      />
    );
  }

  const renderInquiriesPanel = () => {
    const hasActiveFilters = inquirySearch || inquiryStatusFilter !== 'all' || inquiryDateFilter;

    return (
      <section className="dash-panel">
        <div className="dash-panel-head">
          <h2>
            <i className="fas fa-envelope" />
            Inquiries
            <span className="dash-panel-scope">{isAdmin ? 'All records' : 'Your records'}</span>
          </h2>
        </div>

        {/* Inquiries Toolbar: Search & Filters */}
        <div className="dash-table-toolbar">
          <div className="dash-toolbar-filters">
            <div className="dash-search-input-wrap">
              <i className="fas fa-search" />
              <input
                type="text"
                className="dash-search-input"
                placeholder="Search inquiries (name, email, vehicle, phone…)"
                value={inquirySearch}
                onChange={(e) => handleInquirySearchChange(e.target.value)}
              />
              {inquirySearch && (
                <button
                  type="button"
                  className="dash-search-clear-btn"
                  onClick={() => handleInquirySearchChange('')}
                  aria-label="Clear search"
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </div>

            <SearchableSelect
              className="dash-filter-dropdown-wrap"
              value={inquiryStatusFilter}
              onChange={(e) => handleInquiryStatusFilterChange(e.target.value)}
              placeholder="All Statuses"
              aria-label="Filter inquiries by status"
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'new', label: 'New', icon: '✨' },
                { value: 'contacted', label: 'Contacted', icon: '📞' },
                { value: 'answered', label: 'Answered', icon: '💬' },
                { value: 'closed', label: 'Closed', icon: '🔒' },
              ]}
            />

            <div className="dash-date-filter-wrap">
              <span>Date:</span>
              <input
                type="date"
                className="dash-date-filter-input"
                value={inquiryDateFilter}
                onChange={(e) => handleInquiryDateFilterChange(e.target.value)}
                aria-label="Filter by inquiry submission date"
              />
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                className="dash-btn-reset-filters"
                onClick={handleClearInquiryFilters}
              >
                <i className="fas fa-rotate-left" /> Reset
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="dash-loading inline">
            <div className="dash-spinner" />
            <p>Fetching latest data…</p>
          </div>
        ) : filteredInquiries.length === 0 ? (
          <EmptyState
            icon="fa-inbox"
            title="No matching inquiries"
            text={
              hasActiveFilters
                ? 'No inquiries match your current search and filter criteria.'
                : 'Request info from the public site and it will appear here.'
            }
            onClear={hasActiveFilters ? handleClearInquiryFilters : undefined}
          />
        ) : (
          <>
            <div className="dash-table-wrap">
              <div className="dash-table-head inquiries-grid" aria-hidden="true">
                <span>Customer Lead</span>
                <span>Vehicle</span>
                <span>Inquiry Message</span>
                <span>Status</span>
              </div>
              {pagedInquiries.map((row) => {
                const isClosed = (row.status || '').toLowerCase() === 'closed';

                const openInquiry = () => navigate(`/dashboard/inquiries/${row.id}`);
                return (
                  <article
                    key={row.id}
                    className="dash-row inquiries-grid dash-row-clickable"
                    role="link"
                    tabIndex={0}
                    onClick={openInquiry}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') openInquiry();
                    }}
                    title="Open inquiry details"
                  >
                    <div className="dash-row-lead">
                      <div className="dash-row-avatar">{(row.customer_name || '?').charAt(0)}</div>
                      <div>
                        <h3>{row.customer_name}</h3>
                        <div className="dash-record-meta">
                          <span>#{row.id}</span>
                          <span><i className="fas fa-phone" /> {row.phone}</span>
                          {row.email && <span><i className="fas fa-at" /> {row.email}</span>}
                          <span><i className="fas fa-calendar" /> {formatDate(row.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="dash-row-vehicle">
                      {getDisplayVehicle(row) ? (
                        <>
                          <i className="fas fa-car" /> {getDisplayVehicle(row)}
                        </>
                      ) : (
                        <span className="dash-muted">No vehicle</span>
                      )}
                    </div>
                    <p className="dash-row-message">{row.message || '—'}</p>
                    <div className="dash-row-status">
                      <span className="dash-source-chip">{(row.source || 'site').replace(/_/g, ' ')}</span>
                      {isClosed ? (
                        <span className="dash-status-pill status-closed" title="Inquiry Closed (Terminal)">
                          <i className="fas fa-lock" /> Closed
                        </span>
                      ) : (
                        <span className={`dash-status-pill ${statusClass(row.status)}`}>{row.status}</span>
                      )}
                      <button
                        type="button"
                        className="dash-row-view-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openInquiry();
                        }}
                      >
                        <i className="fas fa-eye" /> {isAdmin && !isClosed ? 'View / update' : 'View'}
                      </button>
                      {isAdmin && (
                        <button
                          type="button"
                          className="dash-row-delete-btn"
                          title={`Delete inquiry #${row.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            requestDelete('inquiry', row);
                          }}
                        >
                          <i className="fas fa-trash" /> Delete
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>

            <DataTablePagination
              currentPage={inquiryPage}
              pageSize={inquiryPageSize}
              totalItems={filteredInquiries.length}
              onPageChange={setInquiryPage}
              onPageSizeChange={(newSize) => {
                setInquiryPageSize(newSize);
                setInquiryPage(1);
              }}
            />
          </>
        )}
      </section>
    );
  };

  const renderTestDrivesPanel = () => {
    const hasActiveFilters =
      testDriveSearch ||
      testDriveStatusFilter !== 'all' ||
      testDriveDateFilter ||
      testDriveVehicleFilter !== 'all' ||
      applicationTypeFilter !== 'all';

    return (
      <section className="dash-panel">
        <div className="dash-panel-head">
          <h2>
            <i className="fas fa-folder-open" />
            Applications
            <span className="dash-panel-scope">{isAdmin ? 'All records' : 'Your records'}</span>
          </h2>
        </div>

        {/* Test Drives Toolbar: Search & Filters */}
        <div className="dash-table-toolbar">
          <div className="dash-toolbar-filters">
            <div className="dash-search-input-wrap">
              <i className="fas fa-search" />
              <input
                type="text"
                className="dash-search-input"
                placeholder="Search applications (customer, type, vehicle, phone…)"
                value={testDriveSearch}
                onChange={(e) => handleTestDriveSearchChange(e.target.value)}
              />
              {testDriveSearch && (
                <button
                  type="button"
                  className="dash-search-clear-btn"
                  onClick={() => handleTestDriveSearchChange('')}
                  aria-label="Clear search"
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </div>

            <SearchableSelect
              className="dash-filter-dropdown-wrap"
              value={applicationTypeFilter}
              onChange={(e) => {
                setApplicationTypeFilter(e.target.value);
                setTestDrivePage(1);
              }}
              placeholder="All types"
              aria-label="Filter applications by type"
              options={[
                { value: 'all', label: 'All types' },
                ...Object.entries(APPLICATION_LABELS).map(([value, label]) => ({
                  value,
                  label,
                })),
              ]}
            />

            <SearchableSelect
              className="dash-filter-dropdown-wrap"
              value={testDriveStatusFilter}
              onChange={(e) => handleTestDriveStatusFilterChange(e.target.value)}
              placeholder="All Statuses"
              aria-label="Filter applications by status"
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'pending', label: 'Pending', icon: '⏳' },
                { value: 'contacted', label: 'Contacted', icon: '📞' },
                { value: 'scheduled', label: 'Scheduled', icon: '📅' },
                { value: 'completed', label: 'Completed', icon: '✅' },
                { value: 'cancelled', label: 'Cancelled', icon: '❌' },
              ]}
            />

            {uniqueTestDriveVehicles.length > 0 && (
              <SearchableSelect
                className="dash-filter-dropdown-wrap"
                value={testDriveVehicleFilter}
                onChange={(e) => handleTestDriveVehicleFilterChange(e.target.value)}
                aria-label="Filter test drives by vehicle"
                placeholder="All Vehicles"
                searchPlaceholder="Search vehicle..."
                options={[
                  { value: 'all', label: 'All Vehicles' },
                  ...uniqueTestDriveVehicles.map((v) => ({
                    value: v,
                    label: v,
                    icon: '🚗',
                  })),
                ]}
              />
            )}

            <div className="dash-date-filter-wrap">
              <span>Appointment:</span>
              <input
                type="date"
                className="dash-date-filter-input"
                value={testDriveDateFilter}
                onChange={(e) => handleTestDriveDateFilterChange(e.target.value)}
                aria-label="Filter by test-drive appointment date"
              />
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                className="dash-btn-reset-filters"
                onClick={handleClearTestDriveFilters}
              >
                <i className="fas fa-rotate-left" /> Reset
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="dash-loading inline">
            <div className="dash-spinner" />
            <p>Fetching latest data…</p>
          </div>
        ) : filteredApplications.length === 0 ? (
          <EmptyState
            icon="fa-key"
            title="No matching applications"
            text={
              hasActiveFilters
                ? 'No applications match your current search and filter criteria.'
                : 'Finance, insurance, service, and test-drive applications appear here after you submit them.'
            }
            onClear={hasActiveFilters ? handleClearTestDriveFilters : undefined}
          />
        ) : (
          <>
            <div className="dash-table-wrap">
              <div className="dash-table-head testdrives-grid" aria-hidden="true">
                <span>Customer</span>
                <span>Vehicle</span>
                <span>Requested</span>
                <span>Visit</span>
                <span>Status</span>
              </div>
              {pagedApplications.map((row) => {
                const visitDate = row.scheduled_date || row.preferred_date;
                const visitTime = row.scheduled_time || row.preferred_time;
                return (
                  <article
                    key={row.id}
                    className="dash-row testdrives-grid dash-row-click"
                    onClick={() => navigate(`/dashboard/applications/${row.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') navigate(`/dashboard/applications/${row.id}`);
                    }}
                    role="link"
                    tabIndex={0}
                  >
                    <div className="dash-row-lead">
                      <div className="dash-row-avatar">{(row.customer_name || '?').charAt(0)}</div>
                      <div>
                        <h3>{row.customer_name}</h3>
                        <div className="dash-record-meta">
                          <span className="dash-type-pill">{APPLICATION_LABELS[row.application_type] || 'Test drive'}</span>
                          <span>#{row.id}</span>
                          <span><i className="fas fa-phone" /> {row.phone}</span>
                          {row.email && <span><i className="fas fa-at" /> {row.email}</span>}
                          {row.city && <span><i className="fas fa-location-dot" /> {row.city}</span>}
                          <span><i className="fas fa-calendar" /> {formatDate(row.created_at)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="dash-row-vehicle">
                      {getDisplayVehicle(row) ? (
                        <>
                          <i className="fas fa-car" /> {getDisplayVehicle(row)}
                        </>
                      ) : (
                        <span className="dash-muted">No vehicle</span>
                      )}
                    </div>

                    <div className="dash-row-message">
                      {row.preferred_date || row.preferred_time || row.notes ? (
                        <>
                          {row.preferred_date && <div><strong>Date:</strong> {row.preferred_date}</div>}
                          {row.preferred_time && <div><strong>Time:</strong> {row.preferred_time}</div>}
                          {row.notes && <div className="dash-muted dash-notes">{row.notes}</div>}
                        </>
                      ) : (
                        '—'
                      )}
                    </div>

                    <div>
                      {visitDate || visitTime ? (
                        <div className="dash-scheduled-box">
                          <span className="sched-time">
                            <i className="fas fa-calendar-check" /> {visitDate || 'Date pending'}
                            {visitTime ? ` at ${visitTime}` : ''}
                          </span>
                        </div>
                      ) : (
                        <span className="dash-muted">—</span>
                      )}
                    </div>

                    <div className="dash-row-status">
                      <span className={`dash-status-pill ${statusClass(row.status)}`}>{row.status}</span>
                      {isAdmin && (
                        <button
                          type="button"
                          className="dash-row-delete-btn"
                          title={`Delete application #${row.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            requestDelete('application', row);
                          }}
                        >
                          <i className="fas fa-trash" /> Delete
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>

            <DataTablePagination
              currentPage={testDrivePage}
              pageSize={testDrivePageSize}
              totalItems={filteredApplications.length}
              onPageChange={setTestDrivePage}
              onPageSizeChange={(newSize) => {
                setTestDrivePageSize(newSize);
                setTestDrivePage(1);
              }}
            />
          </>
        )}
      </section>
    );
  };

  return (
    <div className={`dash-shell portal-autocompare${navOpen ? ' nav-open' : ''}`}>
      <header className="dash-mobile-bar">
        <button
          type="button"
          className="dash-menu-btn"
          aria-label={navOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={navOpen}
          onClick={() => setNavOpen((open) => !open)}
        >
          <i className={`fas ${navOpen ? 'fa-xmark' : 'fa-bars'}`} />
        </button>
        <div className="dash-mobile-title">
          <strong>AutoCompare</strong>
          <small>Dashboard</small>
        </div>
      </header>
      {navOpen ? (
        <button
          type="button"
          className="dash-nav-backdrop"
          aria-label="Close menu"
          onClick={() => setNavOpen(false)}
        />
      ) : null}
      <aside className="dash-sidebar">
        <div className="dash-brand">
          <i className="fas fa-car-side" />
          <div>
            <strong>AutoCompare</strong>
            <small>Dashboard</small>
          </div>
          <button
            type="button"
            className="dash-drawer-close"
            aria-label="Close menu"
            onClick={() => setNavOpen(false)}
          >
            <i className="fas fa-xmark" />
          </button>
        </div>
        <nav className="dash-nav">
          <button
            type="button"
            className={location.pathname.startsWith('/dashboard/inquiries') ? 'active' : ''}
            onClick={handleNavigateInquiries}
          >
            <i className="fas fa-envelope" />
            Inquiries
            {stats ? <span className="dash-nav-count">{stats.inquiries}</span> : null}
          </button>
          <button
            type="button"
            className={isTestDrivesRoute ? 'active' : ''}
            onClick={handleNavigateTestDrives}
          >
            <i className="fas fa-folder-open" />
            Applications
            {stats ? <span className="dash-nav-count">{stats.applications}</span> : null}
          </button>
          {isAdmin && (
            <button
              type="button"
              className={isAvailabilityRoute ? 'active' : ''}
              onClick={() => navigate('/dashboard/availability')}
            >
              <i className="fas fa-calendar-days" />
              Availability
            </button>
          )}
          {isAdmin && (
            <div className="dash-nav-group">
              <button
                type="button"
                className={`dash-nav-parent-btn ${isMarketplaceRoute ? 'in-section' : ''}`}
                onClick={handleToggleMarketplace}
                aria-expanded={marketplaceOpen}
              >
                <i className="fas fa-store" />
                <span>Marketplace</span>
                <i
                  className={`fas fa-chevron-right dash-nav-chevron ${marketplaceOpen ? 'rotated' : ''}`}
                />
              </button>
              {marketplaceOpen && (
                <div className="dash-nav-sub">
                  <button
                    type="button"
                    className={`dash-nav-sub-item ${isBrandsRoute ? 'active' : ''}`}
                    onClick={handleNavigateBrands}
                  >
                    <i className="fas fa-tags" />
                    <span>Brands</span>
                  </button>
                  <button
                    type="button"
                    className={`dash-nav-sub-item ${isVehiclesRoute ? 'active' : ''}`}
                    onClick={handleNavigateVehicles}
                  >
                    <i className="fas fa-car" />
                    <span>Vehicles</span>
                    {stats ? <span className="dash-nav-count">{stats.vehicles}</span> : null}
                  </button>
                </div>
              )}
            </div>
          )}
        </nav>
        <div className="dash-sidebar-foot">
          <div className="dash-user-card">
            <div className="dash-user-avatar">{user.name?.charAt(0) || 'U'}</div>
            <div>
              <strong>{user.name}</strong>
              <span className={`role-badge role-${user.role}`}>{user.role}</span>
            </div>
          </div>
          <Link to="/" className="dash-sidebar-link" onClick={handleBackToSite}>
            <i className="fas fa-arrow-left" /> Back to site
          </Link>
          <button type="button" className="dash-sidebar-link dash-logout" onClick={handleLogout}>
            <i className="fas fa-right-from-bracket" /> Logout
          </button>
        </div>
      </aside>

      <main className="dash-main">
        <div className="dash-topbar">
          <div>
            <div className="dash-eyebrow">
              {isBrandsRoute || isVehiclesRoute
                ? 'Content Management System'
                : isAvailabilityRoute
                ? 'Appointment availability'
                : isTestDrivesRoute
                ? 'Application management'
                : 'Welcome back'}
            </div>
            <h1>
              {isBrandsRoute
                ? 'Brand Management'
                : isVehiclesRoute
                ? 'Vehicle Management'
                : isAvailabilityRoute
                ? 'Availability'
                : isTestDrivesRoute
                ? 'Applications'
                : isAdmin
                ? 'Operations overview'
                : 'Your activity'}
            </h1>
            <p className="dash-subtitle">
              {isBrandsRoute
                ? 'Manage automobile brands, view brand models, and add vehicles under brands.'
                : isVehiclesRoute
                ? 'Database content management for all vehicle specifications, pricing, and EV specs.'
                : isAvailabilityRoute
                ? 'Choose which future days and times customers can book.'
                : isTestDrivesRoute
                ? 'Open an application to review its details, documents, and status.'
                : isAdmin
                ? 'Manage vehicle info requests and customer inquiries.'
                : 'Track your inquiries and applications.'}
            </p>
          </div>
          <button type="button" className="dash-refresh-btn" onClick={load} disabled={loading}>
            <i className={`fas fa-rotate ${loading ? 'spin' : ''}`} /> Refresh
          </button>
        </div>

        {error && (
          <div className="dash-alert">
            <i className="fas fa-circle-exclamation" /> {error}
          </div>
        )}

        {stats && (
          <div className="dash-stats-grid">
            <StatCard icon="fa-car" label="Catalog vehicles" value={stats.vehicles} accent="blue" />
            <StatCard icon="fa-envelope-open-text" label="Inquiries" value={stats.inquiries} accent="teal" />
            <StatCard icon="fa-folder-open" label="Applications" value={stats.applications} accent="indigo" />
            <StatCard icon="fa-clock" label="Pending applications" value={stats.pending_applications} accent="amber" />
            <StatCard icon="fa-inbox" label="New inquiries" value={stats.new_inquiries} accent="rose" />
          </div>
        )}

        <Routes>
          <Route index element={<Navigate to="/dashboard/inquiries" replace />} />
          <Route path="/" element={<Navigate to="/dashboard/inquiries" replace />} />
          <Route path="inquiries" element={renderInquiriesPanel()} />
          <Route
            path="inquiries/:inquiryId"
            element={
              <InquiryDetail
                inquiries={inquiries}
                isAdmin={isAdmin}
                loading={loading}
                onStatusChange={handleInquiryStatusChange}
                onDelete={(row) => requestDelete('inquiry', row)}
              />
            }
          />
          <Route path="applications" element={renderTestDrivesPanel()} />
          <Route path="test-drives" element={<Navigate to="/dashboard/applications" replace />} />
          <Route path="test-drives/:applicationId" element={<LegacyApplicationRedirect />} />
          <Route
            path="applications/:applicationId"
            element={
              <ApplicationDetail
                applications={applications}
                isAdmin={isAdmin}
                onStatusChange={handleApplicationStatusChange}
                onSchedule={handleOpenScheduleModal}
                onDelete={(row) => requestDelete('application', row)}
              />
            }
          />
          <Route path="availability" element={isAdmin ? <AvailabilityManager /> : <Navigate to="/dashboard/inquiries" replace />} />
          <Route path="marketplace/brands" element={<BrandManagement />} />
          <Route path="marketplace/brands/:brandId" element={<BrandManagement />} />
          <Route
            path="marketplace/vehicles"
            element={
              <VehicleManagement
                onDirtyChange={setHasUnsavedChanges}
                onClearEditState={load}
              />
            }
          />
          <Route
            path="marketplace/vehicles/:vehicleId/edit"
            element={
              <VehicleManagement
                onDirtyChange={setHasUnsavedChanges}
                onClearEditState={load}
              />
            }
          />
          <Route
            path="marketplace/vehicles/new"
            element={
              <VehicleManagement
                onDirtyChange={setHasUnsavedChanges}
                onClearEditState={load}
              />
            }
          />
          <Route path="*" element={<Navigate to="/dashboard/inquiries" replace />} />
        </Routes>
      </main>

      {/* Test Drive Scheduling / Rescheduling Modal */}
      {isScheduleModalOpen && (
        <ScheduleModal
          application={selectedAppForSchedule}
          isOpen={isScheduleModalOpen}
          onClose={() => {
            setIsScheduleModalOpen(false);
            setSelectedAppForSchedule(null);
          }}
          onScheduleSuccess={handleScheduleSubmit}
        />
      )}

      {/* Terminal Status Confirmation Modal */}
      {statusConfirm && (
        <div
          className="status-confirm-modal-backdrop"
          onClick={handleCancelStatusChange}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="status-confirm-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="status-confirm-modal-header">
              <div className={`status-confirm-icon-wrap ${statusConfirm.isDanger ? 'danger' : 'warning'}`}>
                <i className={`fas ${statusConfirm.icon || (statusConfirm.isDanger ? 'fa-lock' : 'fa-triangle-exclamation')}`} />
              </div>
              <div>
                <h3>{statusConfirm.title}</h3>
                <p>{statusConfirm.message}</p>
              </div>
            </div>

            {statusConfirm.warning && (
              <div className="status-confirm-warning-box">
                <i className="fas fa-circle-exclamation" />
                <span>{statusConfirm.warning}</span>
              </div>
            )}

            <div className="status-confirm-actions">
              <button
                type="button"
                className="status-confirm-btn-cancel"
                onClick={handleCancelStatusChange}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`status-confirm-btn-confirm ${statusConfirm.isDanger ? 'danger' : ''}`}
                onClick={handleConfirmStatusChange}
              >
                {statusConfirm.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
